'use client';

import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import ReactCollapsible from 'react-collapsible';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import Big from 'big.js';

import { PropsWithClassname } from '@/app/types';
import { Reward, RewardVariant } from '@/app/api';
import { formatPriceInCents } from '@/app/utils/formatting';
import { LANG_URL_KEY, REWARD_SEARCH_PARAM_KEY, REWARD_VARIANT_SEARCH_PARAM_KEY } from '@/app/constants';
import { isNumber } from '@/app/utils/numbers';

import Button, { ButtonHTMLTag, ButtonStyle } from '@/app/components/Button/Button';
import RewardItemQuantity from '@/app/components/RewardItemQuantity/RewardItemQuantity';
import Select from '@/app/components/Select/Select';

import { Language } from '@/app/enums';

export enum RewardBoxStyle {
  COMPACT = 'compact',
  COLUMNS = 'columns',
}

interface RewardBoxProps extends PropsWithClassname {
  // Just to identify unique collapsible components on the page
  idPrefix: string;
  reward: Reward;
  selectedVariant?: RewardVariant;
  defaultIsOpen?: boolean;
  disabled?: boolean;
  style?: RewardBoxStyle;
  selectLinkPathname?: string;
  onSelectReward?: () => void;
  isVariantSelectVisible?: boolean;
  isImageAlwaysVisible?: boolean;
  onClickHandler?: () => void;
  isFullScreenDisplay?: boolean;
  isSelectDisabled?: boolean;
  shouldHideControlButtons?: boolean;
}

const RewardBox = ({
  idPrefix,
  reward,
  defaultIsOpen,
  disabled,
  style = RewardBoxStyle.COMPACT,
  selectLinkPathname,
  onSelectReward,
  selectedVariant,
  isVariantSelectVisible: canDisplayVariantSelect,
  isImageAlwaysVisible,
  className,
  onClickHandler,
  isFullScreenDisplay,
  isSelectDisabled,
  shouldHideControlButtons,
}: RewardBoxProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(defaultIsOpen ?? false);
  const defaultVariantToSelect = reward.variants?.find((variant) => variant.id === selectedVariant?.id);
  const [variantToSelectId, setVariantToSelectId] = useState(defaultVariantToSelect?.id);

  const handleTriggerClick = useCallback(() => {
    if (disabled) {
      return;
    }

    if (onClickHandler) {
      return onClickHandler();
    }

    setIsOpen((current) => !current);
  }, [disabled, setIsOpen]);

  const handleSelectVariant = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setVariantToSelectId(event?.target.value);
    },
    [setVariantToSelectId],
  );

  const imagePath = useMemo(() => {
    if (!reward.variants?.length || !variantToSelectId) {
      return reward.photo?.sizes.smallCropped.path;
    }

    const variantToSelect = reward.variants.find((variant) => variant.id === variantToSelectId);

    if (!variantToSelect?.photo?.sizes?.smallCropped?.path) {
      return reward.photo?.sizes.smallCropped.path;
    }

    return variantToSelect.photo.sizes.smallCropped.path;
  }, [reward, variantToSelectId]);

  const itemQuantityRemaining = useMemo(() => {
    if (selectedVariant && isNumber(selectedVariant?.quantitySold) && isNumber(selectedVariant?.totalQuantity)) {
      return new Big(selectedVariant.totalQuantity).sub(selectedVariant.quantitySold).toNumber();
    }

    if (isNumber(reward.quantitySold) && isNumber(reward.totalQuantity)) {
      return new Big(reward.totalQuantity).sub(reward.quantitySold).toNumber();
    }

    return null;
  }, [selectedVariant, reward]);

  const totalItemQuantity = useMemo(() => {
    if (selectedVariant && isNumber(selectedVariant.totalQuantity)) {
      return selectedVariant.totalQuantity;
    }

    if (isNumber(reward.totalQuantity)) {
      return reward.totalQuantity;
    }

    return null;
  }, [selectedVariant, reward]);

  const isSelectButtonDisabled = useMemo(() => {
    return isSelectDisabled || (!!reward?.variants.length && !variantToSelectId);
  }, [reward, variantToSelectId, isSelectDisabled]);

  const variantOptions = useMemo(() => {
    if (!reward.variants?.length) {
      return [];
    }

    const filteredVariants = reward.variants.filter((variant) => {
      if (!isNumber(variant.totalQuantity) || !isNumber(variant.quantitySold)) {
        return true;
      }

      return variant.quantitySold < variant.totalQuantity;
    });

    const options = filteredVariants.map((variant) => {
      const nameWithPrice = [
        variant.name,
        isNumber(variant.amountInCents) ? formatPriceInCents(variant.amountInCents) : undefined,
      ]
        .filter(Boolean)
        .join(' - ');

      if (!isNumber(variant.totalQuantity) || !isNumber(variant.quantitySold)) {
        return {
          value: variant.id,
          label: nameWithPrice,
        };
      }

      const isDisabled = isSelectDisabled || variant.quantitySold >= variant.totalQuantity;
      const quantityString = `${new Big(variant.totalQuantity).sub(variant.quantitySold).toNumber()} / ${variant.totalQuantity}`;

      const nameWithQuantity = [nameWithPrice, quantityString].join(' - ');

      return {
        value: variant.id,
        label: nameWithQuantity,
        disabled: isDisabled,
      };
    });

    return [{ value: undefined, label: t('components.RewardBox.pickAVariant') }, ...options];
  }, [reward.variants]);

  const selectLink = useMemo(() => {
    if (!selectLinkPathname) {
      return;
    }

    const nextSearchParams = {
      [REWARD_SEARCH_PARAM_KEY]: reward.id,
      [REWARD_VARIANT_SEARCH_PARAM_KEY]: variantToSelectId,
      [LANG_URL_KEY]: language === Language.EN ? language : undefined,
    };

    const nextSearchParamsSanitized = Object.entries(nextSearchParams).filter(([_key, value]) => Boolean(value));

    const nextSearchString = `?${nextSearchParamsSanitized.map(([key, value]) => `${key}=${value}`).join('&')}`;

    return `${selectLinkPathname}${nextSearchString}`;
  }, [selectLinkPathname, variantToSelectId, reward, language]);

  const areAllItemsSold = useMemo(() => {
    if (typeof reward.totalQuantity !== 'number' || typeof reward.quantitySold !== 'number') {
      return false;
    }

    return reward.quantitySold === reward.totalQuantity;
  }, [reward]);

  const isItemQuantityVisible = isNumber(itemQuantityRemaining) && isNumber(totalItemQuantity);
  const isRewardDescriptionVisible = !!reward.rewardDescription;
  const areSelectControlsVisible = !!selectLink && !areAllItemsSold && !shouldHideControlButtons;
  const isVariantSelectVisible = canDisplayVariantSelect && !!reward.variants.length;

  return (
    <div
      onClick={handleTriggerClick}
      className={classNames(className, 'w-full transition-all', {
        'mb-[16px] rounded-md border border-neutral-light shadow-md bg-white': !isFullScreenDisplay,
        'flex flex-col lg:flex-row lg:items-stretch lg:justify-stretch mb-0': style === RewardBoxStyle.COLUMNS,
        block: style !== RewardBoxStyle.COLUMNS,
        'cursor-pointer hover:shadow-lg': !disabled && !isFullScreenDisplay,
        'opacity-50': areAllItemsSold,
      })}
    >
      {!!imagePath && (
        <ReactCollapsible
          trigger={''}
          triggerElementProps={{ id: `${idPrefix}-reward-${reward.id}-box-image-trigger` }}
          contentElementId={`${idPrefix}-reward-${reward.id}-box-image-content`}
          transitionTime={300}
          easing="ease"
          open={isImageAlwaysVisible || isOpen}
          containerElementProps={{
            className: classNames('bg-secondary-light', {
              'lg:p-[16px] lg:w-[456px] lg:shrink-0 lg:grow-0': style === RewardBoxStyle.COLUMNS,
            }),
          }}
        >
          <img src={imagePath} alt={reward.name} className="max-w-full h-auto" />
        </ReactCollapsible>
      )}
      <div className={classNames(className, 'relative px-[16px] lg:px-[32px] py-[24px] lg:py-[40px] grow')}>
        <span className="block text-[10px] lg:text-[12px] font-medium uppercase tracking-[3px] leading-[24px] lg:tracking-[4.5px] mb-[16px]">
          <Trans
            i18nKey={'components.RewardBox.donate'}
            values={{ amount: formatPriceInCents(selectedVariant?.amountInCents ?? reward.amountInCents) }}
          >
            Přispějte
            <span className="text-primary-mid">
              {formatPriceInCents(selectedVariant?.amountInCents ?? reward.amountInCents)} a více
            </span>
          </Trans>
        </span>
        <h3 className="text-[20px] leading-[24px] lg:text-[24px] lg:leading-[32px] font-medium mb-[16px]">
          {reward.name}
        </h3>
        {!!selectedVariant && (
          <h4 className="text-[20px] leading-[24px] lg:text-[24px] lg:leading-[32px] font-medium mb-[16px]">
            {selectedVariant.name}
          </h4>
        )}
        {isItemQuantityVisible && (
          <RewardItemQuantity
            itemsRemaining={itemQuantityRemaining}
            totalItems={totalItemQuantity}
            className={classNames({
              hidden: isOpen || style === RewardBoxStyle.COLUMNS,
            })}
          />
        )}
        <ReactCollapsible
          trigger={''}
          triggerElementProps={{ id: `${idPrefix}-reward-${reward.id}-box-text` }}
          contentElementId={`${idPrefix}-reward-${reward.id}-box-content-text`}
          contentInnerClassName={classNames({
            'lg:flex justify-between items-center': !imagePath && style === RewardBoxStyle.COLUMNS,
          })}
          transitionTime={300}
          easing="ease"
          open={isOpen}
          containerElementProps={{ className: classNames({ 'pt-[16px]': isOpen }) }}
        >
          <div
            className={classNames(
              className,
              'reward-box flex flex-column max-h-[2000px] overflow-hidden text-[16px] leading-[24px]',
              {
                'mb-[16px]': style !== RewardBoxStyle.COLUMNS,
              },
            )}
          >
            <object
              // Content from wysiwyg - contains HTML tags
              dangerouslySetInnerHTML={{ __html: reward.description }}
            />
          </div>
          <div className="flex align-center justify-between mb-[16px]">
            {isRewardDescriptionVisible && (
              <span className="text-[16px] leading-[24px] font-medium">{t('components.RewardBox.reward')}</span>
            )}
            {isItemQuantityVisible && (
              <RewardItemQuantity
                itemsRemaining={itemQuantityRemaining}
                totalItems={totalItemQuantity}
                className={classNames({
                  'lg:absolute lg:top-[40px] lg:right-[32px]': style === RewardBoxStyle.COLUMNS,
                })}
              />
            )}
          </div>
          {isRewardDescriptionVisible && (
            <div
              className={classNames(
                className,
                'reward-box flex flex-column max-h-[2000px] overflow-hidden text-[16px] leading-[24px]',
                {
                  'mb-[16px]': style !== RewardBoxStyle.COLUMNS,
                },
              )}
            >
              <object
                // Content from wysiwyg - contains HTML tags
                dangerouslySetInnerHTML={{ __html: reward.rewardDescription }}
              />
            </div>
          )}
          {areSelectControlsVisible && (
            <div
              className={classNames({
                'lg:flex lg:flex-col lg:justify-end lg:ml-[40px]': !imagePath && style === RewardBoxStyle.COLUMNS,
              })}
            >
              {isVariantSelectVisible && (
                <Select
                  name={`${reward.id}-variants`}
                  value={variantToSelectId}
                  onChange={handleSelectVariant}
                  options={variantOptions}
                />
              )}
              <Button
                testId={`reward-${reward.id}-select-button`}
                tagSpecificProps={{
                  htmlTag: ButtonHTMLTag.LINK,
                  href: selectLink,
                  onClick: onSelectReward,
                  disabled: isSelectButtonDisabled,
                }}
                style={ButtonStyle.PRIMARY_OUTLINED}
                className="block whitespace-nowrap w-full text-center px-[16px] py-[12px] lg:px-[24px] lg:py-[20px]"
              >
                {t('components.RewardBox.select')}
              </Button>
            </div>
          )}
        </ReactCollapsible>
      </div>
    </div>
  );
};

export default RewardBox;
